/* Analise por categoria: uma categoria olhada atraves da base inteira.
 *
 * As outras abas cortam o dado por usuario. Esta corta por categoria, que e a pergunta
 * que o produto faz o tempo todo e que ninguem conseguia responder sem abrir 29 paginas
 * e somar a mao: o que E telecom aqui dentro, quem tem, com quem se gasta, quanto por mes.
 *
 * A selecao de usuarios e um filtro so, e vale para a tela inteira: os blocos agregados
 * somam quem esta marcado, e a tabela "por usuario" mostra a decomposicao. Marcar um
 * usuario so transforma a pagina no estudo daquele usuario, sem tela separada.
 */

import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, qs } from "../lib/api";
import type { CategoryRow, CategoryStudy } from "../lib/api";
import { FLOWS, brl2, cell, compact, fmtMes, intBR, mean, pct, shortId, shortName }
  from "../lib/format";
import { Sparkline } from "../charts/Sparkline";
import { HeatMatrix } from "../charts/HeatMatrix";
import { EntryPanel } from "../components/EntryPanel";
import { Card, ChartFrame, ErrorState, PageHead, Skeleton, Tiles } from "../components/ui";
import { useUsers } from "../components/Shell";

const DEFAULT_CATEGORY = "DESPESA - TELECOM MOVEL";

function band(from: number, to: number | null) {
  const label = (v: number) => v >= 1000 ? `${v / 1000} mil` : String(v);
  return to === null ? `${label(from)}+` : `${label(from)}–${label(to)}`;
}

export function Categories() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: users } = useUsers();

  const category = params.get("categoria") || DEFAULT_CATEGORY;
  const picked = params.get("usuarios") || "";
  const month = params.get("mes") || "";
  const showDetail = params.get("detalhe") === "1";

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (key === "categoria") { next.delete("mes"); next.delete("detalhe"); }
    setParams(next, { replace: true });
  };

  const selected = new Set(picked ? picked.split(",") : []);
  const toggleUser = (userId: string) => {
    const next = new Set(selected);
    next.has(userId) ? next.delete(userId) : next.add(userId);
    set("usuarios", [...next].join(","));
  };

  const catalog = useQuery({
    queryKey: ["categories"],
    queryFn: () => api<CategoryRow[]>("/api/categories"),
    staleTime: 5 * 60_000,
  });
  const study = useQuery({
    queryKey: ["category-study", category, picked],
    queryFn: () => api<CategoryStudy>(
      `/api/categories/study${qs({ category, users: picked })}`),
    placeholderData: (previous) => previous,
  });

  const data = study.data;
  const realUsers = useMemo(
    () => (users ?? []).filter((u) => !u.is_aggregate), [users]);

  const tiles = useMemo(() => {
    if (!data) return [];
    const t = data.totals;
    return [
      { key: "massa", label: "Massa total", value: t.amount, unit: "BRL",
        flow: data.flow,
        detail: `${intBR(t.rows)} lançamentos · ticket médio R$ ${compact(t.ticket)}` },
      { key: "mensal", label: "Média mensal", value: t.monthly_mean, unit: "BRL",
        detail: `mediana R$ ${compact(t.monthly_median)} · ${t.months} meses` },
      { key: "usuario", label: "Média por usuário", value: t.user_mean, unit: "BRL",
        detail: `mediana R$ ${compact(t.user_median)} · ${t.users_with_category} de ${data.users.total} têm` },
      { key: "peso", label: `Peso no ${data.flow.toLowerCase()}`,
        value: t.share_of_flow, unit: "ratio",
        detail: "da massa do mesmo fluxo, nos usuários selecionados" },
      { key: "recorrente", label: "Recorrente",
        value: data.traits.recurring_pct, unit: "ratio",
        detail: `${intBR(data.traits.recurring)} de ${intBR(data.traits.recurring_answered)} descrições avaliadas` },
    ];
  }, [data]);

  const matrixRows = useMemo(() => (data?.matrix.rows ?? []).map((r) => ({
    key: r.user_id, label: r.short_id, flow: data!.flow,
    values: r.values, total: r.total,
  })), [data]);

  const flowOf = (c: string) => c.split(" - ")[0];
  const grouped = useMemo(() => FLOWS.map((flow) => ({
    flow,
    items: (catalog.data ?? []).filter((c) => flowOf(c.category) === flow),
  })).filter((g) => g.items.length), [catalog.data]);

  const monthly = data?.monthly ?? [];
  const cutIndex = monthly.findIndex((m) => !m.realized);
  const label = shortName(category);

  return (
    <>
      <PageHead eyebrow="análise por categoria" title={label}>
        A mesma categoria vista na base inteira: quem tem, quanto por mês, com quais
        empresas e em que cidades. O filtro de usuários vale para a página toda.
      </PageHead>

      <div className="controls">
        <label>categoria
          <select value={category} onChange={(e) => set("categoria", e.target.value)}>
            {grouped.map((group) => (
              <optgroup key={group.flow} label={group.flow.toLowerCase()}>
                {group.items.map((c) => (
                  <option key={c.category} value={c.category}>
                    {shortName(c.category)} ({c.users} usuários · R$ {compact(c.amount)})
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
        <a className="btn" href={`/api/categories/monthly.csv${qs({ category, users: picked })}`}>
          baixar CSV mensal
        </a>
      </div>

      <div className="controls">
        <span style={{ fontSize: 12.5, color: "var(--ink-2)" }}>usuários</span>
        <div className="chips" role="group" aria-label="Filtrar usuários">
          <button className="chip" aria-pressed={selected.size === 0}
                  onClick={() => set("usuarios", "")}>
            todos ({realUsers.length})
          </button>
          {realUsers.map((u) => (
            <button key={u.user_id} className="chip"
                    aria-pressed={selected.has(u.user_id)}
                    title={`${u.months} meses · R$ ${compact(u.total)}`}
                    onClick={() => toggleUser(u.user_id)}>
              {shortId(u.user_id)}
            </button>
          ))}
        </div>
      </div>

      {study.isPending && <Skeleton rows={5} height={64} />}
      {study.error && <ErrorState error={study.error} onRetry={() => study.refetch()} />}

      {data && (
        <>
          <Tiles items={tiles} />

          <div style={{ marginTop: 18 }}>
            <ChartFrame
              title="Gasto mensal"
              sub={`${monthly.length} meses · ${data.users.all
                ? `todos os ${data.users.total} usuários`
                : `${data.users.selected.length} usuário(s) selecionado(s)`} · clique num mês para ver os lançamentos`}
              guide="rotina"
              tableHeader={["mês", "valor", "lançamentos", "usuários"]}
              tableRows={monthly.map((m) =>
                [fmtMes(m.month, true), m.value, m.rows, m.users])}
            >
              {!!monthly.length && (
                <Sparkline
                  labels={monthly.map((m) => m.month)}
                  values={monthly.map((m) => m.value)}
                  cutIndex={cutIndex < 0 ? monthly.length : cutIndex}
                  flow={data.flow}
                  title={label}
                  height={190}
                />
              )}
              <div className="chips" style={{ marginTop: 10 }}>
                {monthly.map((m) => (
                  <button key={m.month} className="chip"
                          aria-pressed={month === m.month}
                          onClick={() => { set("mes", month === m.month ? "" : m.month); }}
                          title={`R$ ${brl2(m.value)} · ${m.rows} lançamentos · ${m.users} usuários`}>
                    {fmtMes(m.month)}
                  </button>
                ))}
              </div>
            </ChartFrame>
          </div>

          <div style={{ marginTop: 18 }}>
            <Card
              title="Por usuário"
              sub="quem tem esta categoria, quanto gasta e quanto ela pesa no fluxo dele"
            >
              <table>
                <thead>
                  <tr>
                    <th scope="col">usuário</th>
                    <th scope="col" className="num">total</th>
                    <th scope="col" className="num">média mensal</th>
                    <th scope="col" className="num">meses</th>
                    <th scope="col" className="num">lançamentos</th>
                    <th scope="col" className="num">% do {data.flow.toLowerCase()}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="subtotal">
                    <th scope="row">média dos {data.totals.users_with_category}</th>
                    <td className="num">{cell(data.totals.user_mean)}</td>
                    {/* Média das médias mensais de cada um. Dividir o total médio pelos
                        meses da seleção daria outro número, menor, e sem sentido: os
                        meses da seleção não são os meses de ninguém. */}
                    <td className="num">{cell(mean(data.by_user.map((u) => u.monthly_mean)))}</td>
                    <td className="num">{cell(mean(data.by_user.map((u) => u.months)))}</td>
                    <td className="num">{cell(mean(data.by_user.map((u) => u.rows)))}</td>
                    <td className="num">{pct(data.totals.share_of_flow)}</td>
                  </tr>
                  {data.by_user.map((u) => (
                    <tr key={u.user_id}>
                      <th scope="row" style={{ fontWeight: 400 }}>
                        <button className="chip"
                                style={{ border: 0, padding: 0, background: "none" }}
                                onClick={() => navigate(`/usuarios/${u.user_id}?detalhe=${encodeURIComponent(category)}`)}>
                          {u.short_id}
                        </button>
                      </th>
                      <td className="num">{cell(u.total)}</td>
                      <td className="num">{cell(u.monthly_mean)}</td>
                      <td className="num">{u.months}</td>
                      <td className="num">{intBR(u.rows)}</td>
                      <td className="num">{pct(u.share_of_flow)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>

          {!!matrixRows.length && (
            <div style={{ marginTop: 18 }}>
              <Card title="Usuário × mês"
                    sub="a mesma série, aberta por usuário — a primeira linha é o total do mês">
                <HeatMatrix
                  firstColumnLabel="usuário"
                  columns={data.matrix.months.map((m) => ({ key: m, label: fmtMes(m) }))}
                  rows={matrixRows}
                  normalize="column"
                  heat
                  summaryRows={[{
                    key: "total",
                    label: "total do mês",
                    flow: data.flow,
                    values: data.monthly.map((m) => m.value),
                    total: data.totals.amount,
                  }]}
                  onPickRow={(userId) =>
                    navigate(`/usuarios/${userId}?detalhe=${encodeURIComponent(category)}`)}
                  onPickCell={(_, monthKey) => set("mes", monthKey)}
                />
              </Card>
            </div>
          )}

          <div style={{ marginTop: 18 }}>
            <Card
              title="Entidades desta categoria"
              sub="o que a ontologia extraiu das descrições — empresas, setores, cidades"
            >
              {!data.entities.length && (
                <p style={{ color: "var(--ink-3)", margin: 0 }}>
                  Nenhuma entidade extraída nesta seleção.
                </p>
              )}
              <div style={{ display: "grid", gap: 18,
                            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}>
                {data.entities.map((group) => {
                  const max = Math.max(...group.items.map((i) => i.amount), 1);
                  return (
                    <div key={group.type_key}>
                      <h3 style={{ fontSize: 12.5, margin: "0 0 8px",
                                   display: "flex", justifyContent: "space-between" }}>
                        <span>{group.label}</span>
                        <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>
                          {intBR(group.entities)} distintas
                        </span>
                      </h3>
                      <div style={{ display: "grid", gap: 5 }}>
                        {group.items.map((item) => (
                          <div key={item.name}
                               style={{ display: "grid",
                                        gridTemplateColumns: "minmax(0,1fr) 84px",
                                        gap: 8, alignItems: "center", fontSize: 12 }}
                               title={`${item.name}: ${intBR(item.rows)} descrições · R$ ${brl2(item.amount)}`}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ overflow: "hidden", textOverflow: "ellipsis",
                                            whiteSpace: "nowrap" }}>{item.name}</div>
                              <div style={{ height: 5, background: "var(--surface-2)",
                                            borderRadius: 2, overflow: "hidden" }}>
                                <div style={{ height: "100%",
                                              width: `${(item.amount / max) * 100}%`,
                                              background: "var(--accent)" }} />
                              </div>
                            </div>
                            <span className="mono" style={{ textAlign: "right",
                                                            color: "var(--ink-3)" }}>
                              {cell(item.amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>

          <div style={{ marginTop: 18, display: "grid", gap: 18,
                        gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))" }}>
            <Card title="Distribuição dos lançamentos"
                  sub="por faixa de valor, em módulo — o tamanho do que se gasta">
              <table>
                <thead>
                  <tr>
                    <th scope="col">faixa (R$)</th>
                    <th scope="col" className="num">lançamentos</th>
                    <th scope="col" className="num">massa</th>
                    <th scope="col">&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {data.histogram.map((b) => {
                    const max = Math.max(...data.histogram.map((x) => x.rows), 1);
                    return (
                      <tr key={b.from}>
                        <td className="mono" style={{ fontSize: 12 }}>{band(b.from, b.to)}</td>
                        <td className="num">{intBR(b.rows)}</td>
                        <td className="num">{cell(b.amount)}</td>
                        <td style={{ width: 110 }}>
                          <span style={{ display: "block", height: 8,
                                         background: "var(--surface-2)", borderRadius: 2 }}>
                            <span style={{ display: "block", height: "100%",
                                           width: `${(b.rows / max) * 100}%`,
                                           background: "var(--accent)", borderRadius: 2 }} />
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>

            <Card title="Maiores descrições"
                  sub={`${intBR(data.traits.descriptions)} descrições distintas · ${intBR(data.traits.installments)} parceladas`}>
              <table>
                <thead>
                  <tr>
                    <th scope="col">descrição</th>
                    <th scope="col">usuário</th>
                    <th scope="col" className="num">valor</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_descriptions.map((d) => (
                    <tr key={d.id}>
                      <td style={{ maxWidth: 300 }}>
                        <span className="mono" style={{ fontSize: 12 }}>{d.description}</span>
                        {d.flag && (
                          <span className={`tag ${d.flag.severity}`} style={{ marginLeft: 6 }}
                                title={`${d.flag.rule} → ${d.flag.suggested_category ?? "?"}`}>
                            {d.flag.rule}
                          </span>
                        )}
                      </td>
                      <td className="mono" style={{ fontSize: 12 }}>{d.short_id}</td>
                      <td className="num">{cell(d.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>

          <div style={{ marginTop: 18 }}>
            {showDetail || month ? (
              <EntryPanel
                base="/api/categories/entries"
                params={{ category, users: picked || undefined, month: month || undefined }}
                title={`Lançamentos de ${label}${month ? ` · ${fmtMes(month, true)}` : ""}`}
                sub={data.users.all ? "todos os usuários"
                                    : `${data.users.selected.length} usuário(s)`}
                expected={month
                  ? monthly.find((m) => m.month === month)?.value ?? null
                  : data.totals.amount}
                csvHref={`/api/categories/entries.csv${qs({
                  category, users: picked, month })}`}
                onClose={() => {
                  const next = new URLSearchParams(params);
                  next.delete("mes");
                  next.delete("detalhe");
                  setParams(next, { replace: true });
                }}
                showUser
              />
            ) : (
              <button className="btn" onClick={() => set("detalhe", "1")}>
                ver os {intBR(data.totals.rows)} lançamentos
              </button>
            )}
          </div>
        </>
      )}
    </>
  );
}
